import {Form} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useContext} from 'react';

import {processSetupKeys} from '../../../pages/esgManager/constants/cbam/IronandSteel/ironandSteelQuestionsKeys';
import {generateGoodsSerialNumberOptions} from '../../../pages/esgManager/constants/cbam/IronandSteel/ironandSteelTabs';
import SubmitDisabledContext from '../../../pages/esgManager/constants/SubmitDisabledContext';
import QuestionnaireSwitch from '../../../pages/esgManager/utils/QuestionnaireSwitch';

import {uniqueId} from './uniqueId';

export const RouteFieldWithDynamicOptions: React.FC<{
  formField: any;
  parentFormInstance: ReturnType<typeof useFormInstance>;
  getDynamicOptions: (formField: any, tableAData: any[]) => any[];
  selectedYear: string | null;
  replaceCurrentAndPrevious: (text: string, year: string) => string;
}> = ({
  formField,
  parentFormInstance,
  getDynamicOptions,
  selectedYear,
  replaceCurrentAndPrevious
}) => {
  const [options, setOptions] = React.useState<any[]>([]);

  React.useEffect(() => {
    const updateOptions = () => {
      const tableAData = (parentFormInstance.getFieldValue([
        processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A
      ]) ?? []) as any[];
      const dynamicOptions = getDynamicOptions(formField, tableAData);
      setOptions(dynamicOptions);
    };

    updateOptions();

    const interval = setInterval(() => {
      updateOptions();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [formField, parentFormInstance, getDynamicOptions]);

  return (
    <QuestionnaireSwitch
      field={{
        fieldType: formField.type,
        fieldLabel: replaceCurrentAndPrevious(
          formField.fieldLabel,
          selectedYear ?? ''
        ),
        textLimit: formField.textLimit,
        options,
        heading: replaceCurrentAndPrevious(
          formField.heading,
          selectedYear ?? ''
        ),
        colSpan: formField.colSpan,
        restrictFutureDateSelection: formField.restrictFutureDateSelection,
        fieldName: formField.fieldName,
        units: formField.units,
        dateFormat: formField.dateFormat,
        relatedKey: formField.relatedKey,
        disabled: formField.disabled,
        disabledOn: formField.disabledOn,
        requiredOn: formField.requiredOn,
        fileUploadAction: formField.fileUploadAction,
        fileUploadValidExtensions: formField.fileUploadValidExtensions,
        fileUploadMaxSize: formField.fileUploadMaxSize,
        fileUploadAllowMultipleFiles: formField.fileUploadAllowMultipleFiles,
        conditions: formField.conditions
      }}
    />
  );
};

// Helper function to get dynamic options for Route fields
export const getDynamicOptions = (formField: any, tableAData: any[]): any[] => {
  if (formField?.dynamicOptionsFromTableA) {
    const count = Array.isArray(tableAData) ? tableAData.length : 0;

    return generateGoodsSerialNumberOptions(count);
  }

  return formField.options;
};

// Function to sync table A data to table B
export const syncTableAToTableB = (
  tableAData: any[],
  currentTableBData: any[],
  fieldName: string,
  setDataSource: React.Dispatch<React.SetStateAction<any[]>>,
  setFieldsValueFn: (values: any) => void,
  setIsSubmitDisabled: (disabled: boolean) => void,
  isTableB: boolean
): void => {
  if (
    !isTableB ||
    !tableAData ||
    !Array.isArray(tableAData) ||
    tableAData.length === 0
  ) {
    return;
  }

  const goodsCategoryFieldName =
    processSetupKeys.PROCESS_SETUP_QUESTION_1_6_2_A;
  const productionProcessFieldName =
    processSetupKeys.PROCESS_SETUP_QUESTION_1_6_2_B;

  const existingValues = new Set(
    currentTableBData
      .map((row: any) => row[productionProcessFieldName])
      .filter((val: any) => val != null && val !== '')
  );

  const currentTableAValues = new Set<string>();

  tableAData.forEach((row: any) => {
    const value = row[goodsCategoryFieldName];
    if (value && typeof value === 'string' && value.trim() !== '') {
      currentTableAValues.add(value);
    }
  });

  let updatedTableBData = [...currentTableBData];
  let hasChanges = false;

  // 1) Remove any rows in table B that no longer exist in table A
  const filteredTableBData = updatedTableBData.filter((row: any) => {
    const productionProcessValue = row[productionProcessFieldName];

    return (
      productionProcessValue &&
      currentTableAValues.has(productionProcessValue as string)
    );
  });

  if (filteredTableBData.length !== updatedTableBData.length) {
    hasChanges = true;
    updatedTableBData = filteredTableBData;
  }

  // Find values that need new rows (don't exist in table B yet)
  const valuesToProcess = Array.from(currentTableAValues).filter(
    (value) => !existingValues.has(value)
  );

  // Create new rows for values that don't exist in table B
  const newRows: any[] = [];
  valuesToProcess.forEach((goodsCategoryValue) => {
    // Generate unique ID for new row
    const newRow: any = {
      _id: `new${uniqueId(updatedTableBData)}`,
      [productionProcessFieldName]: goodsCategoryValue
    };

    newRows.push(newRow);
    hasChanges = true;
  });

  // Update table B if there are any changes
  if (hasChanges) {
    const finalData = [...updatedTableBData, ...newRows];
    setDataSource(finalData);
    setFieldsValueFn({[fieldName]: finalData});
    setIsSubmitDisabled(false);
  }
};

// Component to handle table synchronization watchers
export const DynamicTableSync: React.FC<{
  fieldName: string;
  dataSource: any[];
  setDataSource: React.Dispatch<React.SetStateAction<any[]>>;
  isTableB: boolean;
}> = ({fieldName, dataSource, setDataSource, isTableB}) => {
  const setIsSubmitDisabled = useContext(SubmitDisabledContext);

  return (
    <>
      <Form.Item
        noStyle
        shouldUpdate
        dependencies={[processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A]}
      >
        {({
          getFieldValue: getFieldValueFromForm,
          setFieldsValue: setFieldsValueFromForm
        }) => {
          // Get table A data using getFieldValue
          const tableAData = (getFieldValueFromForm(
            processSetupKeys.PROCESS_SETUP_QUESTION_1_6_1_A
          ) ?? []) as any[];

          // Get current table B data
          const currentTableBData = getFieldValueFromForm([fieldName]) ?? [];

          // Sync table A to table B
          syncTableAToTableB(
            tableAData,
            currentTableBData,
            fieldName,
            setDataSource,
            setFieldsValueFromForm,
            setIsSubmitDisabled,
            isTableB
          );

          return null;
        }}
      </Form.Item>
      <Form.Item noStyle shouldUpdate dependencies={[fieldName]}>
        {({getFieldValue: getFieldValueFromForm}) => {
          // Update dataSource when table B data changes (from sync or delete)
          const currentTableBData = getFieldValueFromForm([fieldName]) ?? [];
          if (
            JSON.stringify(currentTableBData) !== JSON.stringify(dataSource)
          ) {
            setDataSource(currentTableBData);
          }

          return null;
        }}
      </Form.Item>
    </>
  );
};
